import React from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

const columns = 3;
const cellSize = 4;
const glyphSize = columns * cellSize;

/**
 * FNV-1a over the OD number, chosen because it is short, stable and needs no dependency - not for
 * any cryptographic property. Shared by the cell pattern and the hue so both stay deterministic per
 * OD number.
 * @param odNumber
 */
function hash(odNumber: string): number {
  let value = 2166136261;
  for (let index = 0; index < odNumber.length; index++) {
    value ^= odNumber.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return value >>> 0;
}

/**
 * Six bits from the hash: one per column of the top row, which the bottom row mirrors, and one per
 * column of the middle row.
 * @param value
 */
function patternBits(value: number): boolean[] {
  return Array.from({ length: columns * 2 }, (_, bit) => ((value >>> bit) & 1) === 1);
}

/**
 * A hue derived from the same hash, distinct from the bits used for the cell pattern so the two
 * don't visibly correlate. Saturation/lightness are fixed rather than hash-derived, so every glyph
 * stays similarly legible against `action.hover` regardless of which hue it lands on.
 * @param value
 */
function patternColor(value: number): string {
  const hue = (value >>> 8) % 360;
  return `hsl(${hue}, 65%, 70%)`;
}

/**
 * A 12x12 mark derived from the OD number, so a reader recognises a row's order by shape and colour
 * before reading anything. The design calls it the licence glyph.
 *
 * The hue is hash-derived rather than palette-derived: it isn't meant to encode anything, only to
 * make one mark easier to tell apart from and remember against another.
 *
 * The mark is hidden from assistive technology, so **the caller must print the OD number as text
 * beside it**. A 12-pixel square nobody can select or read is not an identifier; it only helps a
 * reader who can already see the OD number find the row again.
 * @param root0
 * @param root0.odNumber
 * @param root0.sx
 */
export function LicenceGlyph({ odNumber, sx }: { odNumber: string; sx?: SxProps<Theme> }) {
  const value = hash(odNumber);
  const bits = patternBits(value);
  const color = patternColor(value);
  const cells: { x: number; y: number }[] = [];

  for (let column = 0; column < columns; column++) {
    if (bits[column]) {
      cells.push({ x: column * cellSize, y: 0 });
      cells.push({ x: (columns - 1 - column) * cellSize, y: 2 * cellSize });
    }
    if (bits[columns + column]) {
      cells.push({ x: column * cellSize, y: cellSize });
    }
  }

  return (
    <Box
      component="svg"
      aria-hidden="true"
      data-testid="licence-glyph"
      viewBox={`0 0 ${glyphSize} ${glyphSize}`}
      width={glyphSize}
      height={glyphSize}
      sx={[{ flex: "none", borderRadius: 0.5, bgcolor: "action.hover", color }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {cells.map((cell) => (
        <rect
          key={`${cell.x}-${cell.y}`}
          x={cell.x}
          y={cell.y}
          width={cellSize}
          height={cellSize}
          fill="currentColor"
        />
      ))}
    </Box>
  );
}
